import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "@/locales/en.json";
import sv from "@/locales/sv.json";

export const SUPPORTED_LOCALES = ["en", "sv"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = "tack:lang";

/**
 * Resolve initial locale.
 *
 * Priority:
 *  1. Explicit user choice in localStorage (`tack:lang`)
 *  2. `navigator.language` — Swedish → `sv`, everything else → `en`
 *  3. `sv` default for this project (Sweden-first launch)
 */
function resolveInitialLocale(): Locale {
  if (typeof window === "undefined") return "sv";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "sv" || saved === "en") return saved;
  } catch {
    /* ignore */
  }
  const lang = (navigator.language || "").toLowerCase();
  if (lang.startsWith("sv")) return "sv";
  // Default to SV as we are Sweden-first, even for non-SV browsers
  return "sv";
}

if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    resources: {
      en: { translation: en },
      sv: { translation: sv },
    },
    lng: resolveInitialLocale(),
    fallbackLng: "sv",
    interpolation: { escapeValue: false },
    returnNull: false,
  });
}

/** Call once on client mount to sync i18next with the user's locale. */
export function applyClientLocale() {
  if (typeof window === "undefined") return;
  const target = resolveInitialLocale();
  if (i18n.language !== target) {
    void i18n.changeLanguage(target);
  }
  document.documentElement.lang = target;
}

/** Persist and apply a new locale. */
export function setLocale(locale: Locale) {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
}

export function getCurrentLocale(): Locale {
  return (i18n.language?.startsWith("sv") ? "sv" : "en") as Locale;
}

export default i18n;
